// src/firebase.ts
import { initializeApp } from "firebase/app";
import { getFirestore, getDocFromServer, doc } from "firebase/firestore";

// Manually provided Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyASz8BsYyCYOqUBKA89XOOhhIkFxb3FNxM",
  authDomain: "ai-studio-applet-webapp-6fcd7.firebaseapp.com",
  projectId: "ai-studio-applet-webapp-6fcd7",
  storageBucket: "ai-studio-applet-webapp-6fcd7.firebasestorage.app",
  messagingSenderId: "317610731089",
  appId: "1:317610731089:web:1b0c3faf25b09f85c33a70",
  firestoreDatabaseId: "ai-studio-a0ce6722-d366-41d0-93ba-d549e2e18949"
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);

/**
 * App Check (reCAPTCHA v3) — set VITE_RECAPTCHA_SITE_KEY after enabling
 * App Check in Firebase Console. Without the key, App Check is skipped so
 * local/dev still works until console setup is complete.
 *
 * Debug token (local only):
 *   self.FIREBASE_APPCHECK_DEBUG_TOKEN = true
 * then register the printed token in Firebase Console → App Check → Manage debug tokens.
 */
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;
if (typeof window !== "undefined" && recaptchaSiteKey) {
  void import("firebase/app-check")
    .then(({ initializeAppCheck, ReCaptchaV3Provider }) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
          import.meta.env.VITE_APPCHECK_DEBUG_TOKEN === "true" ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN;
      }
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(recaptchaSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    })
    .catch((err) => {
      console.warn("App Check init skipped:", err);
    });
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: undefined,
      email: null,
      emailVerified: undefined,
      isAnonymous: undefined,
      tenantId: undefined,
      providerInfo: [],
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    // Expected deny under hardened rules for unknown collection — ignore permission errors
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('permission') || msg.includes('Permission')) {
      return;
    }
    console.error("Firestore connection failed: ", error);
    if (msg.includes('the client is offline') || msg.includes('Failed to fetch')) {
      console.error("Please check your Firebase configuration or network connectivity.");
    }
  }
}
testConnection();

export default app;
